import * as React from 'react';

import { DescriptionPage_MarketingSource as MarketingSource } from '__generated__/graphql-types';

import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AdminBannerNotConfigurable from 'bundles/unified-description-page-common/components/admin/AdminBannerNotConfigurable';
import ConsumerCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerCourseEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/page-navigation/NavCoBrandingPartnerLogos';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import CoursePageSEO from 'bundles/unified-description-page-common/components/seo/CoursePageSEO';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/course/GoogleCoursePageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  COURSE_NAV_ITEMS,
  getCoursePageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';

const GoogleCoursePageRootContent = () => {
  const pageData = useCoursePageData();
  const productId = getProductId('course', pageData?.id);

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    // @ts-expect-error URL: string
    photoUrl: pageData?.photoUrl,
    id: productId,
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.courseType,
  });

  if (!pageData) {
    return null;
  }

  const partnersToDisplay = getPartnersForCourse(pageData.partners, pageData.activeSpecialization);
  const productTitle = getProductTitle(pageData.name);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      {
        name: ModuleNames.ProductOverview,
      },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.CareerCertificate,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Reviews,
      },
      {
        name: ModuleNames.Faqs,
      },
    ],
  };

  const navigationLinks = getCoursePageNavigationLinks({
    pageData,
    pageConfig,
  });

  return (
    <PageInfoProvider
      marketingSource={MarketingSource.Google}
      navigationIdMap={COURSE_NAV_ITEMS}
      productSlug={pageData.slug}
    >
      <EventingDataProvider productId={pageData.id} productType="course">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={getVariantTitle()}
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <AdminBannerNotConfigurable
                  courseId={pageData.id}
                  slug={pageData.slug}
                  courseStatus={pageData.courseStatus}
                />
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerCourseEnrollButtonNotConfigurable pageData={pageData} size="small" onlyShowButton />
                  }
                  partnerLogo={
                    partnersToDisplay?.length === 2 ? (
                      <NavCoBrandingPartnerLogos
                        // @ts-expect-error URL: string
                        leftImageSrc={partnersToDisplay[0].squareLogo}
                        leftPartnerName={partnersToDisplay[0].name}
                        // @ts-expect-error URL: string
                        rightImageSrc={partnersToDisplay[1].squareLogo}
                        rightPartnerName={partnersToDisplay[1].name}
                      />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error URL: string
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageConfig} />
                <MobileCtaModule
                  pageCta={
                    <ConsumerCourseEnrollButtonNotConfigurable pageData={pageData} size="small" onlyShowButton />
                  }
                />
                <CoursePageSEO pageData={pageData} disableCrawlerIndexing />
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default GoogleCoursePageRootContent;
